import { Location } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil, switchMap } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { DeepLinkNavService } from 'app/modules/deep-link/deepLinkNav.service';
import { EventsService } from 'app/modules/social-events/services/events.service';
import { AuthService, BasicTempUser } from 'app/services/auth.service';
import { CognitoService } from 'app/services/cognito.service';
import { TermsService } from 'app/services/terms.service';
import { PhoneFormatPipe } from 'app/shared/pipes/phone-format.pipe';
import { Loggable } from 'app/utils/logging/loggable';

@Component({
  selector: 'app-confirm-account',
  templateUrl: './confirm-account.component.html',
  styleUrls: ['./confirm-account.component.scss'],
  providers: [PhoneFormatPipe],
  standalone: false,
})
export class ConfirmAccountComponent extends Loggable implements OnInit, OnDestroy {
  // Context for onIntegration
  @Input() public onIntegration: unknown = null;
  @Input() public doCustomSignUpWithTokens = false;
  @Output() public signUp = new EventEmitter<boolean>();

  public form?: UntypedFormGroup;
  private tempUser?: BasicTempUser;
  private stateURL?: string;
  public successSignUp = false;

  // Handle phone or email input
  public tempLoginInput = '';
  public confirmAccount = true;
  public disableRegisterWithMobile = false;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private authService: AuthService,
    private cognitoService: CognitoService,
    private router: Router,
    private route: ActivatedRoute,
    private deepNavService: DeepLinkNavService,
    private formBuilder: UntypedFormBuilder,
    private termsService: TermsService,
    private eventsService: EventsService,
    private location: Location,
    private store: Store,
  ) {
    super();
    this.setDisplayName('ConfirmAccountComponent');

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.disable_register_with_mobile)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((disableMobile) => {
        this.disableRegisterWithMobile = disableMobile;
      });
  }

  public ngOnInit(): void {
    this.stateURL = this.route.snapshot.queryParamMap.get('return') ?? undefined;
    this.tempUser = this.authService.getUserSignUp();
    this.setLoginInput().catch((err) => this.error('ngOnInit error:', err));

    // Build Form
    this.tempLoginInput = this.tempUser.loginInput;
    this.form = this.formBuilder.group({
      verificationCode: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public onConfirm(): void {
    // Update phone number just in case
    const tempLoginInput = this.getTempLoginInput();

    const { loginInput, password } = this.tempUser ?? {};
    if (!loginInput || !password) {
      this.error('ConfirmAccountComponent.onConfirm(), loginInput or password is null', {
        loginInput,
        password,
      });
      return;
    }
    this.authService.setTempUser(tempLoginInput, password);

    // Verify
    const verificationCode = this.form?.get('verificationCode')?.value;
    this.cognitoService
      .verifySignUpConfirmationCode(loginInput, verificationCode)
      .pipe(
        switchMap(() => this.cognitoService.createVerifiedUser(loginInput, password)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: async ({ cognitoUser, user }) => {
          if (this.doCustomSignUpWithTokens) {
            const expiration = cognitoUser.getSignInUserSession()?.getIdToken().payload.exp;
            localStorage.setItem('exp', JSON.stringify(expiration));
          }
          this.successSignUp = true;
          this.authService.clearTempUser();
          await this.termsService.acceptTerms();

          //In case of an event registration, execute the mapping
          if (this.eventsService.registrationId) {
            this.eventsService
              .transferRegistration(this.eventsService.registrationId)
              .then(() => {
                this.info('Transfer Info: Registration transferred to logged user successfully');
              })
              .catch((err) => {
                this.info('Transfer Info: Error transferring registration to logged user: ', err);
              });
          }

          if (this.onIntegration != null) {
            this.signUp.emit(true);
          } else if (this.stateURL != null) {
            await this.deepNavService.navToDeepLink(this.stateURL, this.router);
          } else {
            await this.router.navigate(['/home']);
          }
        },
        error: (err: unknown) => {
          this.error('onConfirm error:', err);
          if (err instanceof Error) {
            this.form?.controls.verificationCode.setErrors({ validation: err.message });
          }
        },
      });
  }

  private getTempLoginInput(): string {
    return this.tempLoginInput;
  }

  public confirmAccountEvent(input: string): void {
    this.tempLoginInput = input;
  }

  public onResend(): void {
    const { loginInput } = this.tempUser ?? {};
    if (!loginInput) {
      return this.error('onResend: No email or mobile phone found');
    }
    this.cognitoService
      .resendSignUpConfirmation(loginInput)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        error: (err: unknown) => {
          this.error('onResend:', err);
          if (err instanceof Error) {
            this.form?.controls.verificationCode.setErrors({ sendCode: err.message });
          }
          this.error('onResend error', { err });
        },
      });
  }

  public handleBack(): void {
    this.location.back();
  }

  private async setLoginInput(): Promise<void> {
    if (!this.tempUser?.loginInput) {
      await this.router.navigate(['/landing']);
    }
  }

  public get getVerificationCode(): AbstractControl | null {
    return this.form?.get('verificationCode') ?? null;
  }
}
